import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api, API } from "@/lib/api";
import { parseSendResponse } from "@/lib/sendResponse";
import { downloadPdf } from "@/lib/pdfDownload";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ArrowLeft, Send, Copy, Trash2, ExternalLink, Download, DollarSign, History, Undo2, ShieldOff, CreditCard, Mail, Pencil, Camera, X, ImageIcon, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import RecordPaymentDialog, { InvoiceStatusPill, INVOICE_STATUS_META, PAYMENT_METHOD_OPTIONS } from "@/components/RecordPaymentDialog";
import EmailComposeDialog from "@/components/EmailComposeDialog";
import EmailThread from "@/components/EmailThread";
import CustomerCommsPanel from "@/components/CustomerCommsPanel";
import { PaymentDateEditor, fmtPaidDate } from "@/components/PaymentDateEditor";
import { businessDayKey } from "@/lib/businessTz";

const STATUS_ORDER = ["draft", "sent", "viewed", "partial", "paid", "overdue", "void", "cancelled"];

const METHOD_LABELS = {
  credit_card: "Credit card", cash: "Cash", check: "Check", venmo: "Venmo",
  cash_app: "Cash App", apple_pay: "Apple Pay", other: "Other",
  // legacy — will surface bucketed to "Other" in the Payments dashboard
  bank_transfer: "Bank transfer", stripe: "Credit card (Stripe)", manual: "Other", adjustment: "Adjustment",
};

// Canonical bucketer — mirrors backend _canonical_method (invoice_payments.py)
const CANON = new Set(["credit_card", "cash", "check", "venmo", "cash_app", "apple_pay", "other"]);
const canonicalMethod = (m, hasSession) => {
  if (hasSession) return "credit_card";
  const k = (m || "").toLowerCase().trim();
  return CANON.has(k) ? k : "other";
};

const money = (v) => `$${(Number(v || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function InvoiceProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const [inv, setInv] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [payments, setPayments] = useState([]);
  const [audit, setAudit] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [voidTarget, setVoidTarget] = useState(null);
  const [sending, setSending] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  // Guard so the ?action=collect deep-link only auto-opens once per mount
  // even if `inv` re-renders while the dialog is closing.
  const autoCollectHandledRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/invoices/${id}`);
      setInv(data);
      const [c, p, a] = await Promise.all([
        data.customer_id ? api.get(`/customers/${data.customer_id}`) : Promise.resolve({ data: null }),
        api.get(`/invoices/${id}/payments`),
        api.get(`/invoices/${id}/audit-log`),
      ]);
      setCustomer(c.data);
      setPayments(p.data);
      setAudit(a.data);
    } catch { toast.error("Not found"); nav("/app/invoices"); }
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);

  // Deep-link handler for `?action=collect` (from Job Profile → Collect
  // Payment button). Auto-opens the existing RecordPaymentDialog exactly
  // once per mount, and strips the query param with `replace: true` so
  // refreshing the page or hitting back doesn't reopen it.
  //
  // Safe-fails: if the invoice is fully paid/voided/draft or can't accept
  // payments, the dialog is NOT opened — we still strip the query param so
  // the URL is clean. Cross-tenant/deleted invoices already 404 in `load()`
  // and redirect to /app/invoices, so this effect never runs for them.
  useEffect(() => {
    if (!inv || autoCollectHandledRef.current) return;
    const params = new URLSearchParams(location.search);
    if (params.get("action") !== "collect") return;
    autoCollectHandledRef.current = true;

    const balance = Number(inv.balance || 0);
    const isPayable = !["paid", "void", "cancelled", "draft"].includes(inv.status) && balance > 0.005;

    if (isPayable) {
      setPayOpen(true);
    }
    // Always strip the query param — even when we didn't open — so a refresh
    // doesn't get stuck in a "try to open, can't, try again" pattern.
    params.delete("action");
    const cleaned = params.toString();
    nav({ pathname: location.pathname, search: cleaned ? `?${cleaned}` : "" }, { replace: true });
  }, [inv, location.pathname, location.search, nav]);

  const send = async () => {
    if (!inv) return;
    if (sending) return;
    setSending(true);
    let status;
    let data;
    try {
      const resp = await api.post(`/invoices/${id}/send`);
      status = resp.status; data = resp.data;
    } catch (e) {
      status = e?.response?.status; data = e?.response?.data;
    }
    const outcome = parseSendResponse({ status, data, kind: "invoice" });
    if (outcome.kind === "success") {
      if (outcome.document) setInv(outcome.document);
      toast.success(
        <div data-testid="invoice-send-success-toast">
          Emailed to customer{outcome.correlationId ? ` · ref ${outcome.correlationId}` : ""}
        </div>
      );
      load();
    } else {
      showInvoiceSendErrorToast(outcome);
    }
    setSending(false);
  };

  const showInvoiceSendErrorToast = (outcome) => {
    const testid = ({
      provider_failure: "invoice-send-error-toast",
      in_progress: "invoice-send-inprogress-toast",
      already_sent: "invoice-send-alreadysent-toast",
      validation: "invoice-send-validation-toast",
      unknown: "invoice-send-error-toast",
    })[outcome.kind] || "invoice-send-error-toast";
    const publicUrl = outcome.publicUrl || (inv ? `${window.location.origin}/invoice/${inv.share_token}` : null);
    toast.error(
      <div className="space-y-2" data-testid={testid}>
        <div className="text-sm">
          {outcome.message}
          {outcome.correlationId ? ` (ref ${outcome.correlationId})` : ""}
          {outcome.priorCorrelationId ? ` · prior ${outcome.priorCorrelationId}` : ""}
        </div>
        <div className="flex gap-2">
          {publicUrl && (
            <button
              onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Public link copied"); }}
              className="text-xs px-2 py-1 rounded bg-white text-zinc-900 border border-zinc-300"
              data-testid="invoice-send-error-copy-link"
            >Copy public link</button>
          )}
          {outcome.canRetry && (
            <button
              onClick={send}
              className="text-xs px-2 py-1 rounded bg-white text-zinc-900 border border-zinc-300"
              data-testid="invoice-send-retry-btn"
            >Retry</button>
          )}
        </div>
      </div>
    );
  };

  const downloadInvoicePdf = async () => {
    if (!inv) return;
    const url = `${API}/invoices/${inv.id}/pdf?auth=${encodeURIComponent(localStorage.getItem("lf_token") || "")}`;
    const res = await downloadPdf(url, `invoice-${inv.invoice_number || inv.id}.pdf`);
    if (!res.ok) {
      toast.error(
        <div data-testid={`invoice-pdf-${res.kind}-toast`}>
          {res.message}
          <div className="mt-1">
            <button
              onClick={downloadInvoicePdf}
              className="text-xs px-2 py-1 rounded bg-white text-zinc-900 border border-zinc-300"
              data-testid="invoice-pdf-retry-btn"
            >Retry</button>
          </div>
        </div>
      );
    }
  };
  const copyLink = () => {
    if (!inv) return;
    navigator.clipboard.writeText(`${window.location.origin}/invoice/${inv.share_token}`);
    toast.success("Link copied");
  };
  const remove = async () => {
    if (!window.confirm("Delete invoice?")) return;
    try { await api.delete(`/invoices/${id}`); toast.success("Deleted"); nav("/app/invoices"); }
    catch { toast.error("Failed"); }
  };
  const changeStatus = async (status) => {
    // Iter 2026-02: "Paid" status must go through the RecordPaymentDialog so
    // an admin can capture the payment method, check number & photo. Skip
    // the direct status POST when there is still a balance to collect.
    if (status === "paid" && canPay) {
      setPayOpen(true);
      return;
    }
    try {
      await api.post(`/invoices/${id}/status`, { status });
      toast.success(`Status → ${INVOICE_STATUS_META[status]?.label || status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const voidPayment = async (pid, amount, method) => {
    setVoidTarget({ id: pid, amount, method });
  };
  const submitVoid = async ({ reason, photoFileId }) => {
    if (!voidTarget) return;
    try {
      await api.post(`/invoices/${id}/payments/${voidTarget.id}/void`, { reason, void_photo_file_id: photoFileId || null });
      toast.success("Payment voided");
      setVoidTarget(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const resendReceipt = async (pid) => {
    try {
      const { data } = await api.post(`/invoices/${id}/payments/${pid}/resend-receipt`);
      if (data?.ok) {
        toast.success(`Receipt sent to ${data.to}`);
      } else if (data?.error === "no_customer_email") {
        toast.error("Customer has no email — add one to send a receipt.");
      } else {
        toast.error("Receipt could not be sent right now.");
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const updateMethod = async (pid, method) => {
    try {
      await api.patch(`/invoices/${id}/payments/${pid}/method`, { method });
      toast.success("Payment method updated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!inv) return <div className="p-8 text-zinc-500">Loading…</div>;
  const publicUrl = `${window.location.origin}/invoice/${inv.share_token}`;
  const balance = Number(inv.balance || 0);
  const canPay = !["paid", "void", "cancelled", "draft"].includes(inv.status) && balance > 0.005;
  const livePayments = payments.filter((p) => !p.voided);
  const voidedPayments = payments.filter((p) => p.voided);

  return (
    <div className="p-8 max-w-5xl">
      <button onClick={() => nav("/app/invoices")} className="text-sm text-zinc-500 hover:text-zinc-900 flex items-center gap-1 mb-4" data-testid="invoice-back-btn">
        <ArrowLeft className="w-4 h-4"/> All invoices
      </button>

      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div className="min-w-0">
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">{inv.invoice_number}</div>
          <h1 className="font-heading text-4xl font-bold">{inv.title || "Invoice"}</h1>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <InvoiceStatusPill status={inv.status} computed={inv.computed_status} size="md"/>
            <Select value={inv.status} onValueChange={changeStatus}>
              <SelectTrigger className="w-40 h-8 text-xs" data-testid="invoice-status-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>Change to {INVOICE_STATUS_META[s].label}</SelectItem>)}
              </SelectContent>
            </Select>
            {inv.sent_at && <span className="text-xs text-zinc-500">Sent {new Date(inv.sent_at).toLocaleString()}</span>}
            {inv.viewed_at && <span className="text-xs text-indigo-700">Viewed {new Date(inv.viewed_at).toLocaleString()}</span>}
            {inv.paid_at && <span className="text-xs text-emerald-700" data-testid="invoice-paid-date">Paid {fmtPaidDate(businessDayKey(inv.paid_at))}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canPay && (
            <Button onClick={() => setPayOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white btn-lift shadow-lg shadow-emerald-100" data-testid="invoice-mark-paid-btn">
              <DollarSign className="w-4 h-4 mr-1"/> Mark as Paid
            </Button>
          )}
          <Button variant="outline" onClick={copyLink} data-testid="invoice-copy-link-btn"><Copy className="w-4 h-4 mr-1"/> Copy link</Button>
          <Button variant="outline" onClick={downloadInvoicePdf} data-testid="invoice-pdf-btn"><Download className="w-4 h-4 mr-1"/> PDF</Button>
          {!["paid", "void", "cancelled"].includes(inv.status) && <Button variant="outline" onClick={send} disabled={sending} data-testid="invoice-send-btn"><Send className="w-4 h-4 mr-1"/> {sending ? "Sending…" : "Send by email"}</Button>}
          <CustomerCommsPanel
            customerId={inv.customer_id}
            customer={customer}
            invoiceId={inv.id || inv._id}
            triggerLabel="Open SMS thread"
            testId="invoice-open-sms-btn"
          />
          {customer?.email && (
            <Button variant="outline" onClick={() => setComposeOpen(true)} className="border-sky-200 text-sky-700 hover:bg-sky-50" data-testid="invoice-compose-email-btn">
              <Mail className="w-4 h-4 mr-1"/> Compose email
            </Button>
          )}
          <a href={publicUrl} target="_blank" rel="noreferrer"><Button variant="outline"><ExternalLink className="w-4 h-4 mr-1"/> Preview</Button></a>
          <Button variant="outline" onClick={remove} className="text-red-600"><Trash2 className="w-4 h-4"/></Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <TotalCard label="Total" value={money(inv.total)}/>
        <TotalCard label="Paid" value={money(inv.amount_paid)} accent="emerald"/>
        <TotalCard label="Balance due" value={money(inv.balance)} accent={balance > 0 ? "orange" : "muted"} emphasize/>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp">
        <div className="grid grid-cols-2 gap-4 mb-6">
          <EmailComposeDialog
            open={composeOpen}
            onClose={() => setComposeOpen(false)}
            customerId={inv.customer_id}
            invoiceId={inv.id || inv._id}
            prefillTo={customer?.email || ""}
          />
          <div>
            <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500 mb-1">Billed to</div>
            <div className="font-medium">{customer?.name || "—"}</div>
            <div className="text-xs text-zinc-500">{customer?.email}</div>
            <div className="text-xs text-zinc-500">{customer?.address}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500 mb-1">Invoice</div>
            <div className="font-mono-accent">{inv.invoice_number}</div>
            {inv.due_date && <div className="text-xs text-zinc-500">Due {new Date(inv.due_date).toLocaleDateString()}</div>}
          </div>
        </div>

        <table className="w-full text-sm">
          <thead className="border-b border-zinc-200 text-xs uppercase text-zinc-500">
            <tr>
              <th className="text-left py-2">Item</th>
              <th className="text-right py-2">Qty</th>
              <th className="text-right py-2">Unit</th>
              <th className="text-right py-2">Line</th>
            </tr>
          </thead>
          <tbody>
            {inv.line_items.map((li, i) => (
              <tr key={i} className="border-b border-zinc-100">
                <td className="py-3">
                  <div className="font-medium">{li.name}</div>
                  {li.description && <div className="text-xs text-zinc-500">{li.description}</div>}
                  {(li.addons || []).map((a, j) => <div key={j} className="text-xs text-zinc-500 pl-3">+ {a.name} · ${Number(a.price || 0).toFixed(2)}</div>)}
                </td>
                <td className="py-3 text-right font-mono-accent">{li.quantity}</td>
                <td className="py-3 text-right font-mono-accent">${Number(li.unit_price).toFixed(2)}</td>
                <td className="py-3 text-right font-mono-accent">${(Number(li.quantity) * Number(li.unit_price) + (li.addons || []).reduce((s, a) => s + Number(a.price || 0), 0)).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-6 flex justify-end">
          <div className="w-64 space-y-2 text-sm">
            <Row label="Subtotal" value={money(inv.subtotal)}/>
            {inv.discount_amount > 0 && <Row label="Discount" value={`- ${money(inv.discount_amount)}`}/>}
            <Row label={`Tax (${inv.tax_rate || 0}%)`} value={money(inv.tax_total)}/>
            <div className="border-t border-zinc-200 pt-3 flex justify-between">
              <span className="font-heading font-bold">Total</span>
              <span className="font-heading text-3xl font-black text-[#FF5A00]">{money(inv.total)}</span>
            </div>
            {inv.amount_paid > 0 && (
              <div className="flex justify-between text-emerald-700"><span>Amount paid</span><span className="font-mono-accent">- {money(inv.amount_paid)}</span></div>
            )}
            {balance > 0 && (
              <div className="flex justify-between font-bold pt-2 border-t border-zinc-200"><span>Balance due</span><span className="font-mono-accent text-lg">{money(balance)}</span></div>
            )}
          </div>
        </div>
      </div>

      {/* Payments section */}
      <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp mt-4" data-testid="invoice-payments-panel">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500 mb-1">Payments</div>
            <h2 className="font-heading text-xl font-bold flex items-center gap-2"><History className="w-5 h-5 text-emerald-600"/> Payment history</h2>
          </div>
          {canPay && (
            <Button size="sm" onClick={() => setPayOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="invoice-record-payment-btn">
              <DollarSign className="w-4 h-4 mr-1"/> Record payment
            </Button>
          )}
        </div>
        {livePayments.length === 0 && voidedPayments.length === 0 ? (
          <div className="text-sm text-zinc-500 py-6 text-center">No payments yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-zinc-500 border-b border-zinc-100">
              <tr><th className="text-left py-2">Date</th><th className="text-left py-2">Method</th><th className="text-left py-2">Notes / Recorded by</th><th className="text-right py-2">Amount</th><th className="text-right py-2">Actions</th></tr>
            </thead>
            <tbody>
              {livePayments.map((p) => (
                <tr key={p.id} className="border-b border-zinc-100 last:border-0" data-testid={`payment-row-${p.id}`}>
                  <td className="py-3"><PaymentDateEditor invoiceId={id} payment={p} onSaved={load}/></td>
                  <td className="py-3">
                    <PaymentMethodCell payment={p} onChange={(m) => updateMethod(p.id, m)}/>
                    {p.check_number && (
                      <div className="text-[10px] mt-1 font-mono-accent uppercase tracking-widest text-zinc-500" data-testid={`payment-check-num-${p.id}`}>
                        Check #{p.check_number}
                      </div>
                    )}
                    {p.check_photo_file_id && (
                      <CheckPhotoThumb fileId={p.check_photo_file_id} testId={`payment-check-photo-${p.id}`}/>
                    )}
                  </td>
                  <td className="py-3 text-zinc-600 text-xs">
                    {p.notes && <div>{p.notes}</div>}
                    {p.recorded_by_email && <div className="text-zinc-400">{p.recorded_by_email}</div>}
                    {p.session_id && (
                      <div className="mt-1 inline-flex items-center gap-1">
                        <span className="text-[9px] uppercase font-mono-accent tracking-widest px-1 rounded bg-indigo-100 text-indigo-700 border border-indigo-200 inline-flex items-center gap-0.5">
                          <CreditCard className="w-2.5 h-2.5"/> Stripe
                        </span>
                        {p.stripe_url && (
                          <a
                            href={p.stripe_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[10px] font-mono-accent uppercase tracking-widest text-indigo-600 hover:text-indigo-800 hover:underline inline-flex items-center gap-0.5"
                            data-testid={`payment-stripe-link-${p.id}`}
                            title={p.payment_intent_id || p.session_id}
                          >
                            View <ExternalLink className="w-2.5 h-2.5"/>
                          </a>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="py-3 text-right font-mono-accent font-semibold text-emerald-700">{money(p.amount)}</td>
                  <td className="py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => resendReceipt(p.id)}
                        className="text-xs inline-flex items-center gap-1 text-zinc-600 hover:bg-zinc-100 px-2 py-1 rounded"
                        data-testid={`payment-resend-receipt-${p.id}`}
                        title="Resend receipt email to customer"
                      >
                        <Mail className="w-3 h-3"/> Resend receipt
                      </button>
                      {!p.session_id ? (
                        <button onClick={() => voidPayment(p.id, p.amount, p.method)} className="text-xs inline-flex items-center gap-1 text-rose-600 hover:bg-rose-50 px-2 py-1 rounded" data-testid={`payment-void-${p.id}`} title="Void this payment">
                          <Undo2 className="w-3 h-3"/> Void
                        </button>
                      ) : (
                        <span className="text-[10px] text-zinc-400" title="Refund through Stripe">Stripe · refund via Stripe</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {voidedPayments.map((p) => (
                <tr key={p.id} className="border-b border-zinc-100 last:border-0 text-zinc-400" data-testid={`payment-void-row-${p.id}`}>
                  <td className="py-3 font-mono-accent line-through">{p.paid_at ? new Date(p.paid_at).toLocaleDateString() : "—"}</td>
                  <td className="py-3"><span className="inline-block px-2 py-0.5 rounded bg-zinc-100 text-zinc-500 text-xs line-through">{METHOD_LABELS[canonicalMethod(p.method, !!p.session_id)] || "Other"}</span></td>
                  <td className="py-3 text-xs">
                    <div className="inline-flex items-center gap-1"><ShieldOff className="w-3 h-3 text-rose-400"/> Voided {p.voided_at ? new Date(p.voided_at).toLocaleDateString() : ""}</div>
                    {p.voided_reason && <div className="italic">"{p.voided_reason}"</div>}
                    {p.voided_by_email && <div className="text-[10px]">by {p.voided_by_email}</div>}
                    {p.void_photo_file_id && (
                      <CheckPhotoThumb fileId={p.void_photo_file_id} testId={`payment-void-photo-${p.id}`}/>
                    )}
                  </td>
                  <td className="py-3 text-right font-mono-accent line-through">{money(p.amount)}</td>
                  <td className="py-3 text-right"><span className="text-[10px] uppercase font-mono-accent text-rose-400">Voided</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Referral code (Iter43 Phase 3) */}
      <ReferralCodeBlock invoice={inv} onApplied={load}/>

      {/* Phase 2 — Email history (scoped to the customer so invoice + customer emails co-exist) */}
      {inv.customer_id && (
        <div className="mt-4">
          <EmailThread
            customerId={inv.customer_id}
            invoiceId={inv.id || inv._id}
            prefillTo={customer?.email || ""}
            title="Email history"
          />
        </div>
      )}

      {/* Audit log */}
      {audit.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp mt-4" data-testid="invoice-audit-panel">
          <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500 mb-2">Audit trail</div>
          <div className="space-y-2">
            {audit.map((row) => (
              <div key={row.id} className="flex items-start gap-3 text-xs border-b border-zinc-100 last:border-0 py-2">
                <span className="font-mono-accent text-zinc-400 whitespace-nowrap">{row.at ? new Date(row.at).toLocaleString() : ""}</span>
                <span className="uppercase tracking-widest font-mono-accent text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 whitespace-nowrap">{row.action.replace(/_/g, " ")}</span>
                <span className="text-zinc-600 min-w-0 flex-1">
                  {row.detail?.from && row.detail?.to && <span>{row.detail.from} → <b>{row.detail.to}</b></span>}
                  {row.detail?.amount != null && <span> · ${Number(row.detail.amount).toFixed(2)}</span>}
                  {row.detail?.method && <span> · {row.detail.method}</span>}
                  {row.detail?.reason && <span> · "{row.detail.reason}"</span>}
                  {row.detail?.notes && <span className="text-zinc-500"> · {row.detail.notes}</span>}
                </span>
                <span className="text-zinc-400 text-[10px]">{row.actor_email}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <RecordPaymentDialog invoice={inv} open={payOpen} onOpenChange={setPayOpen} onSaved={() => { setPayOpen(false); load(); }}/>
      {voidTarget && (
        <VoidPaymentDialog
          invoiceId={id}
          target={voidTarget}
          onCancel={() => setVoidTarget(null)}
          onSubmit={submitVoid}
        />
      )}
    </div>
  );
}

function Row({ label, value }) { return <div className="flex justify-between"><span className="text-zinc-600">{label}</span><span className="font-mono-accent">{value}</span></div>; }

/**
 * VoidPaymentDialog — Iter 2026-02 "Void with Photo".
 * Replaces the old window.prompt() void flow with a proper dialog:
 * reason textarea + optional supporting photo (NSF letter, bounced check,
 * refund receipt). Photo is uploaded through the same /uploads pipeline
 * used by check-photo capture before the void is posted.
 */
function VoidPaymentDialog({ invoiceId, target, onCancel, onSubmit }) {
  const [reason, setReason] = useState("");
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const isCheck = target?.method === "check";

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const onPhoto = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Photo must be under 10 MB"); return; }
    if (preview) URL.revokeObjectURL(preview);
    setPhoto(f); setPreview(URL.createObjectURL(f));
  };
  const clearPhoto = () => { if (preview) URL.revokeObjectURL(preview); setPhoto(null); setPreview(null); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let fileId = null;
      if (photo) {
        try {
          const fd = new FormData();
          fd.append("file", photo);
          fd.append("resource_type", "invoice");
          fd.append("resource_id", invoiceId);
          const { data: up } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
          fileId = up.id;
        } catch { toast.error("Photo failed to upload — void will still be recorded."); }
      }
      await onSubmit({ reason: reason.trim(), photoFileId: fileId });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="void-payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <ShieldOff className="w-5 h-5 text-rose-500"/> Void payment
          </DialogTitle>
          <DialogDescription>
            Voiding <b>${Number(target?.amount || 0).toFixed(2)}</b> · this will roll back the invoice balance. Attach a supporting document to keep the audit chain intact.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Reason <span className="text-zinc-400 font-normal">(optional but recommended)</span></Label>
            <Textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={isCheck ? "e.g. NSF — check bounced 9/25/2026" : "e.g. Recorded in error"}
              data-testid="void-reason-input"
              autoFocus
            />
          </div>
          <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-3">
            <Label className="flex items-center gap-1.5">
              {isCheck && <AlertTriangle className="w-3.5 h-3.5 text-amber-600"/>}
              Supporting photo <span className="text-zinc-400 font-normal text-xs">(optional)</span>
            </Label>
            {preview ? (
              <div className="relative inline-block mt-1" data-testid="void-photo-preview">
                <img src={preview} alt="Void evidence" className="h-28 rounded border border-zinc-200 object-cover"/>
                <button type="button" onClick={clearPhoto} className="absolute -top-1.5 -right-1.5 bg-white border border-zinc-200 rounded-full p-0.5 shadow" title="Remove" data-testid="void-photo-clear-btn">
                  <X className="w-3 h-3 text-zinc-600"/>
                </button>
              </div>
            ) : (
              <div className="flex gap-2 mt-1">
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-zinc-200 bg-white text-sm cursor-pointer hover:border-rose-400" data-testid="void-photo-camera-btn">
                  <Camera className="w-4 h-4"/> Take photo
                  <input type="file" accept="image/*" capture="environment" onChange={onPhoto} className="hidden"/>
                </label>
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-zinc-200 bg-white text-sm cursor-pointer hover:border-rose-400" data-testid="void-photo-upload-btn">
                  <ImageIcon className="w-4 h-4"/> Upload
                  <input type="file" accept="image/*" onChange={onPhoto} className="hidden"/>
                </label>
              </div>
            )}
            <div className="text-[10px] text-zinc-500 mt-1 font-mono-accent uppercase tracking-widest">
              {isCheck ? "NSF letter · bounced check · deposit slip" : "Refund receipt · adjustment note · correspondence"}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="void-submit-btn">
              {busy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin"/> Voiding…</> : <><ShieldOff className="w-4 h-4 mr-1"/> Void payment</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * CheckPhotoThumb — resolves a signed download URL for a check photo and
 * renders a 40×40 thumbnail that opens the full image in a new tab.
 */
function CheckPhotoThumb({ fileId, testId }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/uploads/${fileId}/signed-url`);
        if (!cancelled) setUrl(data.signed_url || data.url);
      } catch { /* silent — thumbnail simply won't render */ }
    })();
    return () => { cancelled = true; };
  }, [fileId]);
  if (!url) return null;
  const abs = url.startsWith("http") ? url : `${API.replace(/\/api$/, "")}${url}`;
  return (
    <a href={abs} target="_blank" rel="noreferrer" className="inline-block mt-1" data-testid={testId} title="View check photo">
      <img src={abs} alt="Check" className="w-10 h-10 rounded border border-zinc-200 object-cover hover:ring-2 hover:ring-[#FF5A00] transition-all"/>
    </a>
  );
}

/**
 * PaymentMethodCell — displays the canonical bucket for a payment and lets
 * an admin reclassify it inline. Stripe rows are locked (always credit card).
 */
function PaymentMethodCell({ payment, onChange }) {
  const [editing, setEditing] = useState(false);
  const locked = !!payment.session_id;
  const canonical = canonicalMethod(payment.method, locked);
  const label = METHOD_LABELS[canonical] || METHOD_LABELS.other;
  const isLegacy = !locked && canonical === "other" && payment.method && payment.method !== "other";
  if (editing) {
    return (
      <Select
        defaultValue={canonical}
        onValueChange={(v) => { setEditing(false); if (v !== canonical) onChange(v); }}
      >
        <SelectTrigger className="h-7 w-40 text-xs" data-testid={`payment-method-edit-${payment.id}`}><SelectValue/></SelectTrigger>
        <SelectContent>
          {PAYMENT_METHOD_OPTIONS.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  return (
    <div className="inline-flex items-center gap-1">
      <span className={`inline-block px-2 py-0.5 rounded text-xs ${isLegacy ? "bg-amber-50 text-amber-800 border border-amber-200" : "bg-zinc-100 text-zinc-700"}`}
            title={isLegacy ? `Legacy method "${payment.method}" — click pencil to reclassify.` : undefined}>
        {label}{isLegacy && <span className="ml-1 text-[9px] uppercase font-mono-accent tracking-widest opacity-70">legacy</span>}
      </span>
      {!locked && (
        <button
          onClick={() => setEditing(true)}
          className="p-1 rounded hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700"
          title="Change payment method"
          data-testid={`payment-method-edit-btn-${payment.id}`}
        >
          <Pencil className="w-3 h-3"/>
        </button>
      )}
    </div>
  );
}

function TotalCard({ label, value, accent, emphasize }) {
  const c = accent === "emerald" ? "text-emerald-700" : accent === "orange" ? "text-[#FF5A00]" : accent === "muted" ? "text-zinc-400" : "text-zinc-900";
  return (
    <div className={`bg-white border border-zinc-200 rounded-xl p-4 card-crisp ${emphasize ? "ring-2 ring-orange-100" : ""}`}>
      <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500">{label}</div>
      <div className={`font-heading text-3xl font-bold mt-1 ${c}`}>{value}</div>
    </div>
  );
}

/**
 * Iter43 Phase 3 — apply-referral block on the invoice detail page.
 * Only visible for unpaid invoices. Once applied, shows the referrer and
 * the discount amount.
 */
function ReferralCodeBlock({ invoice, onApplied }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const applied = !!invoice.referral_code_used;
  const editable = !["paid", "void", "cancelled"].includes(invoice.status);
  if (!invoice) return null;
  const apply = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${invoice.id}/apply-referral`, { code: code.trim().toUpperCase() });
      toast.success(`Applied — $${Number(data.referral_discount_amount).toFixed(2)} discount`);
      onApplied && onApplied();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to apply code");
    } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp mt-4" data-testid="invoice-referral-block">
      <div className="flex items-center gap-2 mb-2">
        <ExternalLink className="w-4 h-4 text-[#FF5A00]"/>
        <div className="text-sm font-medium">Referral code</div>
      </div>
      {applied ? (
        <div className="text-sm text-emerald-700" data-testid="invoice-referral-applied">
          Applied: <span className="font-mono-accent font-bold">{invoice.referral_code_used}</span>
          {" — "}
          {invoice.referral_discount_pct}% off · ${Number(invoice.referral_discount_amount || 0).toFixed(2)}
        </div>
      ) : editable ? (
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="e.g. JOHS26"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            className="border border-zinc-200 rounded p-2 text-sm font-mono-accent uppercase w-48"
            data-testid="invoice-referral-code-input"
          />
          <Button
            onClick={apply}
            disabled={busy || !code.trim()}
            className="bg-[#FF5A00] hover:bg-[#E04F00] text-white"
            data-testid="invoice-referral-apply-btn"
          >
            Apply code
          </Button>
          <div className="text-xs text-zinc-500">Deducts the referred-customer discount from the subtotal.</div>
        </div>
      ) : (
        <div className="text-sm text-zinc-500">Invoice status locked ({invoice.status}) — no referral code applied.</div>
      )}
    </div>
  );
}

